import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

interface ClipToastState {
  show: boolean;
  type: 'success' | 'error';
  message: string;
}

interface ClipToastContextValue {
  showClipToast: (type: 'success' | 'error', message?: string) => void;
  toastState: ClipToastState;
  hideToast: () => void;
}

const ClipToastContext = createContext<ClipToastContextValue | null>(null);

export function ClipToastProvider({ children }: { children: ReactNode }) {
  const [toastState, setToastState] = useState<ClipToastState>({
    show: false,
    type: 'success',
    message: '',
  });

  const showClipToast = useCallback((type: 'success' | 'error', message?: string) => {
    setToastState({
      show: true,
      type,
      message: message || (type === 'success' ? 'Coupon saved to your account!' : 'Unable to clip coupon.'),
    });
  }, []);

  const hideToast = useCallback(() => {
    setToastState(prev => ({ ...prev, show: false }));
  }, []);

  // Auto-dismiss after 3 seconds
  useEffect(() => {
    if (toastState.show) {
      const timer = setTimeout(() => {
        hideToast();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toastState.show, hideToast]);

  return (
    <ClipToastContext.Provider value={{ showClipToast, toastState, hideToast }}>
      {children}
    </ClipToastContext.Provider>
  );
}

export function useClipToast() {
  const context = useContext(ClipToastContext);
  if (!context) {
    throw new Error('useClipToast must be used within a ClipToastProvider');
  }
  return context;
}
